import { Toaster } from "react-hot-toast";
import Layout from "../shared/Layout";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";
import { API_URL, baseurl } from "../../_config";

const statusOptions = ["upcoming", "active", "live", "completed", "cancelled"];

const initialForm = {
    id: 0,
    tournament_id: "",
    seasonName: "",
    startDate: "",
    endDate: "",
    status: "upcoming",
    description: "",
};

function formatDate(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toISOString().split("T")[0];
}

function formatForInput(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).split("T")[0];
    return date.toISOString().split("T")[0];
}

const YclSeasons = () => {
    const [seasons, setSeasons] = useState([]);
    const [tournaments, setTournaments] = useState([]);
    const [formData, setFormData] = useState(initialForm);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchTournaments = useCallback(async () => {
        try {
            const res = await axios.get(`${baseurl}${API_URL.getTournaments}`);
            setTournaments(res.data?.data || []);
        } catch {
            setTournaments([]);
        }
    }, []);

    const fetchSeasons = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${baseurl}${API_URL.getYclSeasons}`);
            if (res.data?.isSuccess === 1) {
                setSeasons(res.data?.data || []);
            } else {
                setSeasons([]);
            }
        } catch {
            toast.error("Failed to load YCL seasons");
            setSeasons([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTournaments();
        fetchSeasons();
    }, [fetchTournaments, fetchSeasons]);

    const handleClose = () => {
        setIsFormOpen(false);
        setFormData(initialForm);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            const res = await axios.post(`${baseurl}${API_URL.saveYclSeason}`, formData);
            if (res.data?.isSuccess === 1) {
                toast.success(res.data?.message || "Season saved successfully");
                handleClose();
                fetchSeasons();
            } else {
                toast.error(res.data?.message || "Failed to save season");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Error saving season");
        }
    };

    const handleEdit = (season) => {
        setFormData({
            id: season.id || 0,
            tournament_id: season.tournament_id || "",
            seasonName: season.seasonName || "",
            startDate: formatForInput(season.startDate),
            endDate: formatForInput(season.endDate),
            status: season.status || "upcoming",
            description: season.description || "",
        });
        setIsFormOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this season?")) return;

        try {
            const res = await axios.delete(`${baseurl}${API_URL.deleteYclSeason}?id=${id}`);
            if (res.data?.isSuccess === 1) {
                toast.success("Season deleted successfully");
                fetchSeasons();
            } else {
                toast.error(res.data?.message || "Delete failed");
            }
        } catch {
            toast.error("Error deleting season");
        }
    };

    const filteredSeasons = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return seasons;
        return seasons.filter((season) => JSON.stringify(season).toLowerCase().includes(term));
    }, [seasons, searchTerm]);

    const columns = [
        { name: "S.No", selector: (row, index) => index + 1, width: "70px" },
        { name: "Tournament ID", selector: (row) => row.tournament_id || "-", sortable: true, minWidth: "130px" },
        { name: "Tournament", selector: (row) => row.tournamentName || "-", sortable: true, minWidth: "190px" },
        { name: "Season", selector: (row) => row.seasonName || "-", sortable: true, minWidth: "220px" },
        { name: "Start Date", selector: (row) => formatDate(row.startDate), sortable: true, minWidth: "150px" },
        { name: "End Date", selector: (row) => formatDate(row.endDate), sortable: true, minWidth: "150px" },
        { name: "Status", selector: (row) => row.status || "-", sortable: true, minWidth: "140px" },
        {
            name: "Action",
            cell: (row) => (
                <div className="d-flex" style={{ gap: "10px" }}>
                    <span onClick={() => handleEdit(row)} title="Edit" className="edit_icon_wepper">
                        <i className="fa fa-pen" style={{ color: "#9b20ff", cursor: "pointer" }}></i>
                    </span>
                    <span onClick={() => handleDelete(row.id)} title="Delete" className="delete_icon_weepr">
                        <i className="fa fa-trash" style={{ color: "#ff0000", cursor: "pointer" }}></i>
                    </span>
                </div>
            ),
            width: "110px",
        },
    ];

    return (
        <Layout>
            <Toaster position="top-right" />
            <div className="right-content-section">
                <div className="admin-page mt-3 d-sm-flex justify-content-between align-items-center">
                    <h4 className="mb-0">YCL Seasons</h4>
                    <div className="d-md-flex align-items-center justify-content-end gap-3">
                        <div className="search-container">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                className="form-control"
                            />
                        </div>
                        <div className="add_btn">
                            <button onClick={() => setIsFormOpen(true)}>Add Season</button>
                        </div>
                    </div>
                </div>

                <div className="datatable mt-4">
                    <DataTable
                        columns={columns}
                        data={filteredSeasons}
                        progressPending={loading}
                        progressComponent={<p>Loading...</p>}
                        pagination
                        highlightOnHover
                    />
                </div>

                <div id="entryformContainer" className={`adminform entryform ${isFormOpen ? "open" : ""}`}>
                    <div className="closebtn">
                        <button onClick={handleClose}>
                            <i className="fa fa-xmark" style={{ color: "#fff", fontSize: "24px" }}></i>
                        </button>
                    </div>

                    <div className="enForm">
                        <div className="text-center form-heading">
                            <h4>{formData.id ? "Edit Season" : "Add Season"}</h4>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label>Tournament</label>
                                    <select
                                        className="form-control"
                                        name="tournament_id"
                                        value={formData.tournament_id}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Select Tournament</option>
                                        {tournaments.map((tournament) => (
                                            <option key={tournament.id} value={tournament.id}>
                                                {tournament.id} - {tournament.tournamentName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-6">
                                    <label>Season Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="seasonName"
                                        value={formData.seasonName}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label>Start Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="startDate"
                                        value={formData.startDate}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label>End Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="endDate"
                                        value={formData.endDate}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label>Status</label>
                                    <select
                                        className="form-control"
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                    >
                                        {statusOptions.map((status) => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-12">
                                    <label>Description</label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        rows="3"
                                        value={formData.description}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div className="d-flex form-buttons mt-3" style={{ gap: "15px" }}>
                                <div className="submit-btn pt-2">
                                    <button type="submit">{formData.id ? "Update" : "Submit"}</button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default YclSeasons;
